<script setup lang="ts">
import { ref, onMounted, watch } from 'vue';
import Options from '@/components/satisfaction/Options.vue';
import { schema } from '@/components/satisfaction/options';
import { toTypedSchema } from '@vee-validate/zod';
import { useForm } from 'vee-validate';
import { useRoute } from 'vue-router';
import axios from 'axios';
import { Loader2, Ban } from 'lucide-vue-next';
import Description from '@/components/satisfaction/Description.vue';
import Share from '@/components/satisfaction/Share.vue';
import GoogleReview from '@/components/satisfaction/GoogleReview.vue';
import Form from '@/components/satisfaction/Form.vue';

interface SatisfactionData { 
    clinicID: string;
    clinicName: string;
    clinicLogo: string;
    doctorName: string;
    reviewLink: string;
    reviewTarget: string;
    hasComment: boolean;
    wasVerySatisfied: boolean;
}

const formSchema = toTypedSchema(schema);

const { values } = useForm({
    validationSchema: formSchema,
    initialValues: {
        type: undefined,
    },
});

const route = useRoute();
const notFound = ref<boolean>(false);
const submitted = ref<boolean>(false);
const data = ref<SatisfactionData>({
    clinicID: '',
    clinicName: '',
    clinicLogo: '',
    doctorName: '',
    reviewLink: '',
    reviewTarget: '',
    hasComment: false,
    wasVerySatisfied: false,
});
const pageLoader = ref<boolean>(true);

async function getSatisfactionData() {
    const res = await axios.post('/getSatisfactionData', {
        appID: route.query.appID,
    }) as any;

    if (res.status === 'success') { 
        data.value = res.data;
    } else {
        notFound.value = true;
    }

    pageLoader.value = false;
}

onMounted(() => {
    if (route.query.appID) {
        getSatisfactionData();
    } else {
        notFound.value = true;
    }
});

function submitSatisfactionData() {
    const comment = [
        {
            question: 'How was your experience so far?',
            key: 'experience',
            answer: 'Very Satisfied',
        },
    ]
    axios.post('/setSatisfaction', {
        comment: JSON.stringify(comment),
        appID: route.query.appID,
    });   
}

watch(
    () => values.type,
    (newVal) => {
        if (newVal === '5') {
            submitSatisfactionData();
        }
    }
);
</script>

<template>
    <div>
        <Loader2 v-if="pageLoader" class="size-10 animate-spin mx-auto" />
        <div v-else-if="notFound" class="flex flex-col items-center justify-center min-h-[80vh]">
            <Ban class="size-[60px] mx-auto text-red-500" />
            <div class="text-center text-[28px] text-gray-900 font-bold mt-5">Not Found</div>
        </div>
        <div class="min-h-screen flex flex-col" v-else>
            <div class="flex items-center justify-center h-[80px] mb-10">
                <img v-if="data.clinicLogo" :src="data.clinicLogo" class="h-full rounded-md shadow" />
                <div v-else class="font-extrabold text-[22px] text-gray-900">{{ data.clinicName }}</div>
            </div>
            <div v-if="data.wasVerySatisfied">
                <Description type="5" class="mb-10" />
                <Share />
            </div>
            <div v-else-if="data.hasComment" class="flex flex-col items-center justify-center min-h-[70vh]">
                <img src="/thanks.svg" class="size-[60px] mx-auto" />
                <div class="text-center text-[28px] text-gray-900 font-bold mt-5 text-balance leading-snug">You've
                    submitted your opinion before.
                </div>
            </div>
            <div v-else-if="submitted" class="flex flex-col items-center justify-center min-h-[70vh]">
                <img src="/thanks.svg" class="size-[60px] mx-auto" />
                <div class="text-center text-[28px] text-gray-900 font-bold mt-5 text-balance leading-snug">Thank You
                    for Your<br>Feedback!</div>
            </div>
            <div v-else>
                <div v-if="values.type">
                    <Description v-if="values.type" :type="values.type" class="mb-10" />
                    <Share v-if="values.type === '5' && !data.reviewLink" />
                    <GoogleReview v-if="values.type === '5' && data.reviewLink" :review-link="data.reviewLink"
                        :review-target="data.reviewTarget" />
                    <Form v-if="values.type !== '5'" :type="values.type || ''" @submitted="submitted = true" />
                </div>
                <div v-show="values.type === undefined">
                    <div class="font-medium text-lg mb-10 text-gray-800">
                        <p>Hello,</p>
                        <p>We hope you had a great appointment with <span
                                class="font-bold text-gray-900 inline-block">{{ data.doctorName }}</span>.</p>
                    </div>
                    <Options />
                </div>
            </div>
        </div>
    </div>
</template>
